/**
 * VAULT CRYPTO MODULE
 * Extends E2EE for multi-document encryption with:
 * - Per-document random DEKs (not derived)
 * - AAD (Additional Authenticated Data) binding
 * - Key wrapping for storing DEKs in the index
 * - Document envelope format v1
 */

import { E2EE } from './e2ee.js';
import { t } from '../../shared/i18n/index.js';

export const Vault = {
    // Version and constants
    VERSION: 1,
    APP: "orbital",
    AEAD: "A256GCM",

    // Document types
    DOC_TYPE_INDEX: "index",
    DOC_TYPE_MAP: "map",

    // Fixed index document key
    INDEX_DOC_ID: "vault-index",

    // Generate UUID v4
    generateDocId() {
        const bytes = E2EE.randomBytes(16);
        // Set version (4) and variant (RFC4122)
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;

        const hex = Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");

        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    },

    // Generate a random DEK (32 bytes for AES-256)
    generateDEK() {
        return E2EE.randomBytes(32);
    },

    // Build AAD string for a document
    // Format: "orbital:v1:docType:docId"
    buildAAD(docType, docId) {
        return new TextEncoder().encode(
            `${this.APP}:v${this.VERSION}:${docType}:${docId}`,
        );
    },

    // AES-GCM encrypt with AAD
    async encryptWithAAD(key, plaintext, aad) {
        const iv = E2EE.randomBytes(12);

        const cryptoKey = await crypto.subtle.importKey(
            "raw",
            key,
            { name: "AES-GCM" },
            false,
            ["encrypt"],
        );

        const ciphertext = await crypto.subtle.encrypt(
            { name: "AES-GCM", iv, additionalData: aad },
            cryptoKey,
            plaintext,
        );

        return {
            iv: E2EE.toBase64(iv),
            ct: E2EE.toBase64(new Uint8Array(ciphertext)),
        };
    },

    // AES-GCM decrypt with AAD
    async decryptWithAAD(key, iv, ct, aad) {
        const ivBytes = E2EE.fromBase64(iv);
        const ctBytes = E2EE.fromBase64(ct);

        const cryptoKey = await crypto.subtle.importKey(
            "raw",
            key,
            { name: "AES-GCM" },
            false,
            ["decrypt"],
        );

        const plaintext = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: ivBytes, additionalData: aad },
            cryptoKey,
            ctBytes,
        );

        return new Uint8Array(plaintext);
    },

    // Wrap a DEK with the KEK (master key)
    // Returns wrapped key object for storage in index
    async wrapKey(kek, dek, docId) {
        const aad = this.buildAAD("wrap", docId);
        const wrapped = await this.encryptWithAAD(kek, dek, aad);
        return {
            v: this.VERSION,
            iv: wrapped.iv,
            ct: wrapped.ct,
        };
    },

    // Unwrap a DEK using the KEK
    async unwrapKey(kek, wrappedKey, docId) {
        const aad = this.buildAAD("wrap", docId);
        return await this.decryptWithAAD(
            kek,
            wrappedKey.iv,
            wrappedKey.ct,
            aad,
        );
    },

    // Encrypt a document (index or map)
    // Returns envelope object for storage
    async encryptDoc(docType, docId, key, plaintextObj) {
        const aad = this.buildAAD(docType, docId);
        const plaintext = new TextEncoder().encode(
            JSON.stringify(plaintextObj),
        );

        const encrypted = await this.encryptWithAAD(key, plaintext, aad);

        return {
            v: this.VERSION,
            app: this.APP,
            aead: this.AEAD,
            docType: docType,
            docId: docId,
            iv: encrypted.iv,
            ct: encrypted.ct,
        };
    },

    // Decrypt a document envelope
    // Returns parsed plaintext object
    async decryptDoc(envelope, key) {
        // Validate envelope
        if (envelope.v !== this.VERSION) {
            throw new Error(`Unsupported envelope version: ${envelope.v}`);
        }
        if (envelope.aead !== this.AEAD) {
            throw new Error(`Unsupported AEAD: ${envelope.aead}`);
        }

        const aad = this.buildAAD(envelope.docType, envelope.docId);
        const plaintext = await this.decryptWithAAD(
            key,
            envelope.iv,
            envelope.ct,
            aad,
        );

        return JSON.parse(new TextDecoder().decode(plaintext));
    },

    // Create empty index structure
    createEmptyIndex() {
        const now = Date.now();
        const rootFolderId = this.generateDocId();

        return {
            schema: "orbital.index.v1",
            createdAt: now,
            updatedAt: now,
            migrationBackup: null,
            folders: [
                {
                    id: rootFolderId,
                    parentId: null,
                    name: t("vault.myMaps"),
                    createdAt: now,
                    updatedAt: now,
                },
            ],
            maps: [],
        };
    },

    // Create map plaintext structure
    createMapPlaintext(title, state) {
        return {
            schema: "orbital.map.v1",
            titleHint: title,
            state: state,
        };
    },

    // Get root folder ID from index
    getRootFolderId(index) {
        const root = index.folders.find((f) => f.parentId === null);
        return root ? root.id : null;
    },
};
